/**
 * Turning a soft keyboard into something a terminal can be driven from.
 *
 * WHAT IS MISSING ON A PHONE
 * --------------------------
 * A phone's keyboard has letters, digits, some punctuation and Return. It has
 * no Escape, no Tab, no Control, no Alt and no arrows — which is, almost
 * exactly, the set of keys a command line is driven by. An agent CLI waiting on
 * a permission prompt is answered with Escape; a half-typed path is finished
 * with Tab; a run that has to stop is stopped with Ctrl+C; the command you ran
 * two minutes ago is on Up. None of the five can be typed on the device this
 * console is being read from, and the console's own shortcut system is worse
 * than no help here — it lives on F1, F2, F6 and a Cmd plane, and a phone has
 * none of those either.
 *
 * WHY THIS IS A MODULE AND NOT A COMPONENT
 * ----------------------------------------
 * Everything here is a pure function from (key, modifiers) to the bytes a
 * terminal expects, and those bytes are a wire format: `\x1b[1;5A` is
 * Ctrl+Up and nothing else, and an off-by-one in the parameter makes it a
 * different key rather than a broken one. That is exactly the kind of thing
 * that must be testable without a browser, a socket or a rendered pane — so the
 * encoding lives here, the bar in `KeyBar.tsx` only decides what to draw, and
 * the pane only decides where to send it.
 *
 * The sequences are the xterm/VT100 ones, which is what `@xterm/xterm` renders
 * and what tmux on the far end is configured for. They are not invented here:
 * the modifier parameter is the standard `1 + (shift=1) + (alt=2) + (ctrl=4)`,
 * so plain is no parameter at all, Alt is 3, Ctrl is 5 and both is 7.
 */

/** Which modifiers are latched right now. */
export interface Modifiers {
  ctrl: boolean
  alt: boolean
}

export const NO_MODIFIERS: Modifiers = { ctrl: false, alt: false }

/**
 * How a modifier key is being held.
 *
 * Three states rather than a boolean, because a phone has no way to hold a key
 * down while pressing another. `once` is the ordinary tap — it survives exactly
 * one keystroke, which is what Ctrl+C is; `locked` is the double tap, for the
 * operator walking a long file with Ctrl held. Without the lock, reading a log
 * with Ctrl+U and Ctrl+D means tapping Ctrl before every single press.
 */
export type Latch = 'off' | 'once' | 'locked'

export function nextLatch(current: Latch): Latch {
  // off -> once -> locked -> off. A single control cycling three states rather
  // than a tap-and-long-press, because a long press on a phone opens a context
  // menu about as often as it registers.
  if (current === 'off') return 'once'
  if (current === 'once') return 'locked'
  return 'off'
}

/** Whether a latch contributes to the next keystroke. */
export function active(latch: Latch): boolean {
  return latch !== 'off'
}

/** The latch after a key has been sent through it. */
export function spend(latch: Latch): Latch {
  return latch === 'once' ? 'off' : latch
}

/** The standard modifier parameter for a CSI sequence: 1 plus the bits. */
function modifierParameter(mods: Modifiers): number {
  return 1 + (mods.alt ? 2 : 0) + (mods.ctrl ? 4 : 0)
}

/**
 * The control character for a printable one, or null where there is none.
 *
 * Control is not a shift: it clears the top bits, which is why Ctrl+C is 0x03
 * and Ctrl+[ is Escape. The letters are the common case; the six symbols after
 * them are the rest of the C0 range and are here because Ctrl+\ (quit) and
 * Ctrl+[ (escape) are both things an operator reaches for, and neither can be
 * derived from the letter rule.
 */
export function controlCharacter(ch: string): string | null {
  if (ch.length !== 1) return null
  const upper = ch.toUpperCase()
  const code = upper.charCodeAt(0)
  if (code >= 65 && code <= 90) return String.fromCharCode(code - 64) // A..Z -> 1..26
  switch (ch) {
    case '@':
    case ' ':
      return '\x00'
    case '[':
      return '\x1b'
    case '\\':
      return '\x1c'
    case ']':
      return '\x1d'
    case '^':
      return '\x1e'
    case '_':
      return '\x1f'
    case '?':
      return '\x7f'
    default:
      return null
  }
}

/**
 * Applies latched modifiers to something the operator typed on the soft
 * keyboard.
 *
 * Only to a single character, and that bound is the point rather than a
 * limitation. xterm delivers one keystroke as one chunk and a paste as one
 * large one; running a paste through the control mapping would turn a pasted
 * command into a stream of control codes, which is a considerably worse outcome
 * than a modifier that quietly did not apply. A chunk longer than a character
 * is passed through untouched.
 *
 * A modifier with nothing to map — Ctrl and a character that has no control
 * form — also passes the character through rather than swallowing it. Dropping
 * the keystroke would be the interface losing an operator's input to a mode
 * they may not remember being in.
 */
export function applyModifiers(data: string, mods: Modifiers): string {
  if (!mods.ctrl && !mods.alt) return data
  if (data.length !== 1) return data

  let out = data
  if (mods.ctrl) out = controlCharacter(data) ?? data
  // Alt is sent as the escape prefix rather than by setting the high bit. Both
  // encodings exist; this is the one every modern terminal and both agent CLIs
  // read, and the high-bit form is ambiguous the moment anything is UTF-8.
  if (mods.alt) out = '\x1b' + out
  return out
}

/** The keys the bar can send that a soft keyboard has no key for at all. */
export type BarKey =
  | 'escape'
  | 'tab'
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'home'
  | 'end'
  | 'pageUp'
  | 'pageDown'

const CSI_FINAL: Partial<Record<BarKey, string>> = {
  up: 'A',
  down: 'B',
  right: 'C',
  left: 'D',
  home: 'H',
  end: 'F',
}

/**
 * The bytes for one of the bar's keys, under the latched modifiers.
 *
 * Escape and Tab ignore the modifiers on purpose. Ctrl+[ *is* Escape and
 * Ctrl+I *is* Tab, so applying a Ctrl latch to either would send the same byte
 * twice over and leave the latch looking broken; Alt+Escape and Alt+Tab belong
 * to the operating system on both platforms and never reach a web page anyway.
 */
export function sequenceFor(key: BarKey, mods: Modifiers): string {
  if (key === 'escape') return '\x1b'
  if (key === 'tab') return '\t'

  if (key === 'pageUp' || key === 'pageDown') {
    // The tilde family, which carries its modifier after the number rather than
    // in place of it: `\x1b[5~` plain, `\x1b[5;5~` with Ctrl.
    const n = key === 'pageUp' ? 5 : 6
    const p = modifierParameter(mods)
    return p === 1 ? `\x1b[${n}~` : `\x1b[${n};${p}~`
  }

  const final = CSI_FINAL[key]
  if (!final) return ''
  const p = modifierParameter(mods)
  // With no modifier the sequence carries no parameter at all. `\x1b[1A` is
  // legal and means the same thing, but the applications on the far end match
  // on the bare form, and one of them — the Codex CLI's own key table — reads
  // the parameterised version as an unknown sequence and prints it.
  return p === 1 ? `\x1b[${final}` : `\x1b[1;${p}${final}`
}
